import type { AllergyIntolerance } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import { Field } from '../../components/Field';
import type { FhirView } from '../../core/view-contract';
import { resourceDisplayName } from '../accessors';
import { formatFhirDate } from '../observation/format';

import { AllergyEditView } from './AllergyEditView';

/**
 * The exported AllergyIntolerance component. Safety-critical surface: high
 * criticality must be loud, and reactions ride along wherever the row shows.
 */
export const AllergyIntoleranceView: FhirView<AllergyIntolerance> = (props) => {
  if (props.ctx.inList) return <AllergyRowView {...props} />;
  if (props.ctx.mode === 'edit') return <AllergyEditView {...props} />;
  return <AllergyReadView {...props} />;
};

function CriticalityTag({ data }: { data: AllergyIntolerance }) {
  if (data.criticality !== 'high') return null;
  return <Tag pill subtle size="xs" color="negative" label="High risk" />;
}

function reactionText(data: AllergyIntolerance): string | undefined {
  const manifestations = (data.reaction ?? [])
    .flatMap((reaction) => reaction.manifestation ?? [])
    .map((concept) => concept.text ?? concept.coding?.[0]?.display)
    .filter(Boolean);
  return manifestations.length > 0 ? manifestations.join(', ') : undefined;
}

function clinicalStatusCode(data: AllergyIntolerance): string | undefined {
  return data.clinicalStatus?.coding?.[0]?.code;
}

/** Allergy-list row: substance, criticality, reaction manifestations. */
const AllergyRowView: FhirView<AllergyIntolerance> = ({ data }) => {
  const reactions = reactionText(data);
  return (
    <div data-view="allergy-row" className="py-2">
      <Stack direction="horizontal" gap={2} align="center" className="min-w-0">
        <Text inline size="sm" weight="regular" className="truncate">
          {resourceDisplayName(data)}
        </Text>
        <CriticalityTag data={data} />
        <FhirStatusTag status={clinicalStatusCode(data)} />
      </Stack>
      {reactions && (
        <Text size="xs" color="tertiary" className="truncate">
          {reactions}
        </Text>
      )}
    </div>
  );
};

/** Standalone detail card: identity row, fact grid, reactions, notes. */
const AllergyReadView: FhirView<AllergyIntolerance> = ({ data, ctx }) => {
  const reactions = reactionText(data);
  const notes = (data.note ?? []).map((note) => note.text).filter(Boolean);
  return (
    <div
      data-view="allergy-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          fullWidth={false}
          className="min-w-0"
        >
          <Text inline size="sm" weight="semibold" className="truncate">
            {resourceDisplayName(data)}
          </Text>
          <CriticalityTag data={data} />
        </Stack>
        <FhirStatusTag status={clinicalStatusCode(data)} />
      </Stack>
      <Grid columns={2} className="mt-3 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Field label="Criticality" data={data} path={['criticality']} />
        <Field
          label="Verification"
          data={data}
          path={['verificationStatus', 'coding', 0, 'code']}
        />
        <Field
          label="Onset"
          data={data}
          path={['onsetDateTime']}
          render={(value) => formatFhirDate(value)}
        />
        <Field
          label="Recorded"
          data={data}
          path={['recordedDate']}
          render={(value) => formatFhirDate(value)}
        />
      </Grid>
      {reactions && (
        <Text size="xs" color="tertiary" className="mt-3">
          Reactions: {reactions}
        </Text>
      )}
      {notes.map((note, index) => (
        <Text key={index} size="xs" color="tertiary" className="mt-1">
          {note}
        </Text>
      ))}
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
